import styled from 'styled-components';

import { Button, IconButton, SecondaryButton } from '../../../../../../../../components';
import Avatar, { AvatarVariant } from '../../../../../../../../components/Avatar';
import Input from '../../../../../../../../components/Input';
import { ThemeProp } from '../../../../../../../../theme/Themes';
import VehicleInformation from '../../VehicleInformation/VehicleInformation';

interface ContainerProps {
  $isLastStep?: boolean;
}

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-top: 1rem;
  padding-bottom: ${(props: ThemeProp & ContainerProps) => (props.$isLastStep ? '1.5rem' : '0')};
  margin: 0;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    padding-top: 0;
    padding-bottom: ${(props: ThemeProp & ContainerProps) => (props.$isLastStep ? '2rem' : '0')};
    border-bottom: ${(props: ThemeProp & ContainerProps) =>
      props.$isLastStep ? `1px solid ${props.theme.colors.neutrals.c100}` : 'none'};
  }
`;

export const NoSearchResultText = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.ml};
  color: ${(props: ThemeProp) => props.theme.colors.content.subdued.high};
  font-style: italic;
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.xs};
`;

export const SelectedOrgContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  width: 100%;
`;

export const SelectedOrgLabel = styled.span`
  color: ${(props: ThemeProp) => props.theme.colors.content.default};
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.lineHeight};
`;

export const SelectedOrgBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
  color: ${(props: ThemeProp) => props.theme.colors.content.default};
  border-radius: 0.5rem;
`;

export const AvatarImage = styled(Avatar).attrs(() => ({
  variant: AvatarVariant.SMALL,
}))``;

interface ListContainerProps {
  $addBorderTop?: boolean;
}

export const ListContainer = styled.div<ListContainerProps>`
  display: flex;
  flex-direction: column;
  min-height: 19rem;
  max-height: 19rem;
  overflow: auto;
  width: 100%;
  gap: 0.5rem;
  padding: 0 1.5rem;
  border-top: ${(props: ThemeProp & ListContainerProps) =>
    props.$addBorderTop ? `1px solid ${props.theme.colors.neutrals.c100}` : 'none'};

  &:last-child {
    padding-bottom: 1rem;
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    gap: 0.75rem;
    min-height: 17.25rem;
    max-height: 22.25rem;
  }
`;

export const StyledVehicleInformation = styled(VehicleInformation)`
  margin-bottom: 1rem;
`;

export const PaddingContainer = styled.div`
  padding: 0 1.5rem;
  width: 100%;
`;

export const OrganizationsWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0 1.5rem;
`;

export const SearchInputAndButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0 1.5rem;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

export const AddConnectionIconButton = styled(IconButton)`
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  background: ${(props: ThemeProp) => props.theme.gradients.button.default};

  svg {
    path {
      stroke: ${(props: ThemeProp) => props.theme.colors.content.white};
    }
  }

  &:hover {
    background: ${(props: ThemeProp) => props.theme.gradients.button.default};
  }
`;

export const ShowAboveTabletWrapper = styled.div`
  display: flex;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const ShowTabletAndBelowWrapper = styled.div`
  display: none;
  width: 100%;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

export const AddConnectionButton = styled(Button)`
  flex: 1;

  svg {
    path {
      stroke: ${(props: ThemeProp) => props.theme.colors.content.white};
    }
  }
`;

export const AddConnectionStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  gap: 1.5rem;
  padding-bottom: 1.5rem;
`;

export const AddConnectionFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0 1.5rem;
  width: 100%;
  flex: 1;
`;

export const StyledInput = styled(Input).attrs((props) => ({
  ...props,
  css: {
    ...props.css,
    marginBottom: '0 !important',
  },
}))``;

export const StyledButton = styled(Button)`
  width: 50%;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    height: 3rem;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.ml};
  }
`;
export const StyledSecondaryButton = styled(SecondaryButton)`
  width: 50%;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    height: 3rem;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.ml};
  }
`;

export const AddConnectionEmptyViewCTA = styled(StyledButton)`
  width: 100%;
  height: 3rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.ml};
`;

export const AddConnectionFormCTAsContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  width: 100%;
  flex: 1;
  padding: 0 1.5rem;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    padding-top: 1.5rem;
    border-top: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  }
`;

export const ConnectionLabel = styled.span`
  font-size: ${({ theme }: ThemeProp) => theme.fontSize.s};
  line-height: ${({ theme }: ThemeProp) => theme.lineHeight.xs};
  font-style: italic;
  color: ${({ theme }: ThemeProp) => theme.colors.content.subdued.high};
  margin-left: auto;
`;

export const EmptyViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 1.5rem;
  margin-top: 4.125rem;
`;

export const EmptyViewTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`;

export const EmptyViewTitle = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.mlg};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c900};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
`;

export const EmptyViewSubtitle = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.s};
  color: ${(props: ThemeProp) => props.theme.colors.content.subdued.high};
  text-align: center;
  max-width: 18.75rem;
`;

export const EmptyViewCTAsContainer = styled.div``;
